import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStore, OPERATOR_NUMBERS } from '../store/sessionStore.js';
import { pwaApi } from '../lib/api.js';
import StatePicker from './StatePicker.js';

export interface ProductionOrderOption {
  id: number;
  workOrder: string;
  sku: string;
  batchNr: string;
  udm: string;
  productionQuantity: string;
}

export interface PlantUnitOption {
  id: number;
  code: string;
  description: string | null;
}

const NEW_ORDER_VALUE = '__new__';
const TOTAL_STEPS = 4;

const SELECT_CLASS = 'w-full bg-gray-800 text-white text-lg px-4 py-3 rounded border border-gray-600 focus:border-blue-500 focus:outline-none disabled:opacity-50';
const INPUT_CLASS = SELECT_CLASS;
const WIZARD_BUTTON = 'text-white px-10 py-4 rounded-lg font-bold text-lg';

/**
 * Apertura ordine come sequenza guidata (richiesta #5 del file Modifiche20260722.xlsx).
 * Il foglio elencava l'ordine prima del login operatore; su indicazione di Mario (24/07/2026)
 * il login viene invece **prima**: chi sta per lavorare si identifica, poi carica l'ordine.
 *   1) login operatore  2) ordine di processo  3) stato macchina  4) velocità settata
 * Gli operatori scelti al passo 1 vengono registrati appena la sessione esiste (fine passo 2),
 * perché il login è agganciato alla sessione; i passi 3 e 4 scrivono subito sul server.
 */
export default function OrderWizard({ orders, plantUnits, onClose, onOrdersChanged }: {
  orders: ProductionOrderOption[];
  plantUnits: PlantUnitOption[];
  onClose: () => void;
  onOrdersChanged: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const {
    availableStates, sessionId,
    startSession, loginOperator, addStateLog,
  } = useSessionStore();

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pendingOperators, setPendingOperators] = useState<number[]>([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState('');
  const [newWorkOrder, setNewWorkOrder] = useState('');
  const [newQuantity, setNewQuantity] = useState('');
  const [newUdm, setNewUdm] = useState(plantUnits[0]?.code ?? '');
  const [newSku, setNewSku] = useState('');
  const [newBatchNr, setNewBatchNr] = useState('');

  const [stateId, setStateId] = useState<number | null>(null);
  const [speed, setSpeed] = useState('');

  const isNewOrder = selectedWorkOrder === NEW_ORDER_VALUE;
  const canConfirmOrder = isNewOrder
    ? Boolean(newWorkOrder.trim() && newQuantity && newUdm)
    : Boolean(selectedWorkOrder);

  async function handleOrderStep() {
    setBusy(true);
    setError(null);
    try {
      if (isNewOrder) {
        const { data } = await pwaApi.post('/tablet/production-orders', {
          workOrder: newWorkOrder.trim(),
          productionQuantity: Number(newQuantity),
          udm: newUdm,
          sku: newSku.trim() || undefined,
          batchNr: newBatchNr.trim() || undefined,
        });
        await startSession(data.data as ProductionOrderOption);
      } else {
        const order = orders.find(o => o.workOrder === selectedWorkOrder);
        if (!order) return;
        await startSession(order);
      }
      // Gli operatori scelti al passo precedente si registrano ora: il login vive sulla sessione
      for (const n of pendingOperators) await loginOperator(n);
      await onOrdersChanged();
      setStep(3);
    } catch (e) {
      setError(t('order.errorTakeCharge') + ' ' + String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleStateStep() {
    setBusy(true);
    try {
      if (stateId) await addStateLog(stateId);
      setStep(4);
    } finally {
      setBusy(false);
    }
  }

  async function handleSpeedStep() {
    setBusy(true);
    try {
      if (speed && sessionId) {
        await pwaApi.post(`/tablet/sessions/${sessionId}/production`, { type: 'RATE', value: Number(speed) });
      }
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const titles: Record<number, string> = {
    1: t('wizard.title.operator'),
    2: t('wizard.title.order'),
    3: t('wizard.title.state'),
    4: t('wizard.title.speed'),
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(10,10,25,0.94)' }}>
      <div className="bg-gray-900 border border-gray-600 rounded-xl w-full max-w-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-blue-800">
          <div>
            <div className="text-blue-200 text-xs font-bold uppercase tracking-wide">
              {t('wizard.step', { current: step, total: TOTAL_STEPS })}
            </div>
            <h2 className="text-white font-bold text-xl">{titles[step]}</h2>
          </div>
          <button onClick={onClose} className="text-blue-100 hover:text-white text-3xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-4">
          {step === 1 && (
            <div className="grid grid-cols-4 gap-3">
              {OPERATOR_NUMBERS.map(n => {
                const selected = pendingOperators.includes(n);
                return (
                  <button
                    key={n}
                    onClick={() => setPendingOperators(prev => selected ? prev.filter(x => x !== n) : [...prev, n])}
                    className={`h-20 rounded-lg font-bold text-lg ${
                      selected ? 'bg-green-700 text-white ring-2 ring-green-400' : 'bg-gray-800 text-gray-200 hover:bg-gray-700 active:bg-gray-600 border border-gray-700'
                    }`}
                  >
                    {t('operator.short')}{n}
                  </button>
                );
              })}
            </div>
          )}

          {step === 2 && (
            <>
              <select className={SELECT_CLASS} value={selectedWorkOrder} onChange={e => setSelectedWorkOrder(e.target.value)}>
                <option value="" disabled>{t('order.selectPlaceholder')}</option>
                {orders.map(o => (
                  <option key={o.id} value={o.workOrder}>
                    {o.workOrder}{o.sku ? ` — ${o.sku}` : ''}{o.batchNr ? ` (${o.batchNr})` : ''}
                  </option>
                ))}
                <option value={NEW_ORDER_VALUE}>{t('order.newOrderOption')}</option>
              </select>

              {isNewOrder && (
                <div className="grid grid-cols-3 gap-3">
                  <input className={INPUT_CLASS} placeholder={t('order.placeholder')} value={newWorkOrder} onChange={e => setNewWorkOrder(e.target.value)} />
                  <input className={INPUT_CLASS} type="number" placeholder={t('order.quantity')} value={newQuantity} onChange={e => setNewQuantity(e.target.value)} />
                  <select className={SELECT_CLASS} value={newUdm} onChange={e => setNewUdm(e.target.value)} aria-label={t('order.udm')}>
                    <option value="" disabled>{t('order.udm')}</option>
                    {plantUnits.map(u => <option key={u.id} value={u.code}>{u.code}</option>)}
                  </select>
                  <input className={INPUT_CLASS} placeholder={t('order.sku')} value={newSku} onChange={e => setNewSku(e.target.value)} />
                  <input className={INPUT_CLASS} placeholder={t('order.batchNr')} value={newBatchNr} onChange={e => setNewBatchNr(e.target.value)} />
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <StatePicker states={availableStates} value={stateId} onChange={setStateId} selectClassName={SELECT_CLASS} showLabels />
          )}

          {step === 4 && (
            <input
              className={INPUT_CLASS}
              type="number"
              placeholder={t('speed.new')}
              value={speed}
              onChange={e => setSpeed(e.target.value)}
            />
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            {step === 1 && (
              <button onClick={() => setStep(2)} disabled={busy} className={WIZARD_BUTTON + ' bg-blue-600 hover:bg-blue-700'}>
                {t('wizard.next')}
              </button>
            )}
            {step === 2 && (
              <button
                onClick={handleOrderStep}
                disabled={!canConfirmOrder || busy}
                className={WIZARD_BUTTON + ' bg-green-600 hover:bg-green-700 disabled:bg-gray-700'}
              >
                {t('wizard.next')}
              </button>
            )}
            {step === 3 && (
              <button onClick={handleStateStep} disabled={busy} className={WIZARD_BUTTON + ' bg-blue-600 hover:bg-blue-700'}>
                {t('wizard.next')}
              </button>
            )}
            {step === 4 && (
              <button onClick={handleSpeedStep} disabled={busy} className={WIZARD_BUTTON + ' bg-green-600 hover:bg-green-700'}>
                {t('wizard.finish')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
